import * as Sentry from '@sentry/react';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import Lockr from 'lockr';
import { LocalStorageKeys, PagesName } from '../constants';
import Config, { ConfigLoaded, Cookies } from '../types/Config';
import { PageConfig } from '../types/api/PageConfig';
import { PostItem } from '../types/api/Posts';
import StorageAffiliates from '../types/state/StorageAffiliates';
dayjs.extend(customParseFormat);

export const sortAscending = (a: number, b: number) => a - b;
export const sortDescending = (a: number, b: number) => b - a;

export function throttle(func: Function, limit: number): Function {
  let inThrottle: boolean;

  return function (this: any): any {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      inThrottle = true;
      func.apply(context, args);
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

export const stringToMiliseconds = (time: string): number | null => {
  const parsed = time.split(':');
  if (parsed.length === 3) {
    return miliseconds(Number(parsed[0]), Number(parsed[1]), Number(parsed[2]));
  }
  return null;
};
export const miliseconds = (
  hrs: number = 0,
  min: number = 0,
  sec: number = 0,
): number => {
  return (hrs * 60 * 60 + min * 60 + sec) * 1000;
};
export const removeFalsyFromObject = (obj: any) =>
  Object.keys(obj).forEach(
    k => !obj[k] && obj[k] !== undefined && delete obj[k],
  );

export const structuredBankCommunications = (barcode): string => {
  if (!barcode) return '';
  const rExp = /\d+/;
  const moduloOf97 = barcode.match(rExp)[0] % 97;
  return '00' + barcode + moduloOf97.toString().padStart(2, '0');
};

export const formatNavigationRoutes = (constants: PageConfig) => {
  // TODO: Remove when descrepencies around constants updating are resolved
  // Temporary solution to get around a blocker.
  // I intend to remove it but I do not trust that the discrepancies between environments will be resolved quickly.
  const fallbackTwoFactorAuthRoute = {
    path: '/two-factor-auth',
    id: 51,
    name: 'nav_link_two_factor_auth',
    exact: false,
  };

  const navigationRoutes = constants.navigation_routes
    ? [...constants.navigation_routes]
    : [];
  navigationRoutes.push(fallbackTwoFactorAuthRoute);
  if (constants.content_pages) {
    for (const [key, value] of Object.entries(constants.content_pages)) {
      const name = Array.isArray(value) ? value[0] : value;
      const slug = Array.isArray(value) ? value[1] : key;
      const contentPagePath = `${slug.startsWith('/') ? '' : '/'}${slug}`;
      if (!navigationRoutes.some(route => route.path === contentPagePath)) {
        navigationRoutes.push({
          id:
            Array.isArray(value) && PagesName[key]
              ? Number(key)
              : PagesName.TemplatePage,
          path: contentPagePath,
          name,
        });
      }
    }
  }
  return navigationRoutes.sort((a, b) =>
    sortDescending(a.path.length, b.path.length),
  );
};

export const clearUserLocalStorage = () => {
  const keys = [
    'transactions-date-to',
    'transactions-date-from',
    'geocomplyRetryCount',
    'winnings-refresh-tracker',
    'userCompanyId',
    'geoComplyValidation',
    'constantsCache',
    'translationsCache',
  ];
  for (const key of keys) {
    localStorage.removeItem(key);
  }
};

export const cleanPostBody = (obj: { [key: string]: unknown }) =>
  Object.entries(obj).reduce((obj, [key, value]) => {
    if (!key.includes('temp_field_') && value != null) {
      obj[key] = typeof value === 'string' ? value.trim() : value;
    }
    return obj;
  }, {});

export const errorHandler = (event: ErrorEvent) => {
  if (event.error?.stack?.includes('kambi-chunk')) {
    const kambiErrorId = 'kambi-error-reload';
    const kambiErrorRetryCount = Lockr.get(kambiErrorId, 0) + 1;
    if (kambiErrorRetryCount < 3) {
      Sentry.captureMessage(`Kambi chunk error`, {
        level: Sentry.Severity.Critical,
        extra: event.error,
      });
      Lockr.set(kambiErrorId, kambiErrorRetryCount);
      window.location.reload();
    }
  }
};

export const filterPromotionsList = (promotions: PostItem[]): PostItem[] =>
  // TODO: Move this into the api
  promotions.filter(promotion => {
    const isNotExpired =
      !promotion.expiration_date ||
      dayjs(promotion.expiration_date).isAfter(dayjs());
    const isPublished =
      promotion.publish_date && dayjs(promotion.publish_date).isBefore(dayjs());
    const isVisible = promotion.visible;
    const isForWeb = promotion.show_for !== 2;
    return isNotExpired && isPublished && isVisible && isForWeb;
  });

export const mergeDeep = (target, ...sources) => {
  if (!sources.length) return target;
  const source = sources.shift();

  const isObject = item => {
    return item && typeof item === 'object' && !Array.isArray(item);
  };

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        mergeDeep(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }

  return mergeDeep(target, ...sources);
};

export const getCachedConfig = (): Config => {
  const defaultCookies: Cookies = {
    accepted: false,
    functional: true,
    analytics: false,
    marketing: false,
    personalization: false,
  };
  const savedCookieSettings = Lockr.get(
    LocalStorageKeys.cookies,
    defaultCookies,
  );
  const savedLocale = Lockr.get(LocalStorageKeys.locale, null);
  const mobileView = !!new URLSearchParams(window.location.search).get(
    'mobile-view',
  );
  return {
    locales: [],
    routes: [],
    locale: savedLocale,
    configLoaded: ConfigLoaded.Loading,
    cookies: savedCookieSettings,
    showPageLoader: true,
    domLoaded: false,
    mobileView,
  };
};
const affiliatesStorageId = 'affiliates';
export const getQueryAffiliates = () => {
  const params = new URLSearchParams(window.location.search);
  const storageAffiliates = Lockr.get<StorageAffiliates>(
    affiliatesStorageId,
    {},
  );
  if (
    params.has('btag') &&
    (!storageAffiliates.btag?.validUntil ||
      dayjs().isBefore(dayjs(storageAffiliates.btag.validUntil)))
  ) {
    Lockr.set(affiliatesStorageId, {
      ...storageAffiliates,
      btag: {
        value: params.get('btag'),
        validUntil: dayjs().add(30, 'day').toJSON(),
      },
    });
  }
};
export const getActiveAffiliates = (): { [key: string]: string } => {
  const storageAffiliates = Lockr.get<StorageAffiliates>(
    affiliatesStorageId,
    {},
  );
  return Object.entries(storageAffiliates).reduce((obj, [key, value]) => {
    if (
      !value.validUntil ||
      dayjs().isBefore(dayjs(storageAffiliates.btag.validUntil))
    ) {
      obj[key] = value.value;
    }
    return obj;
  }, {});
};
export const setDateTime = (date: Dayjs, hours, minutes, seconds) => {
  return date.set('hour', hours).set('minute', minutes).set('second', seconds);
};

/**
 * Resolves a timezone identifier that is safe to pass to Intl / dayjs.tz().
 * An invalid or misconfigured `jurisdictionTimezone` (e.g. a non-IANA name
 * such as "Canada/Alberta") makes Intl.DateTimeFormat throw a RangeError,
 * which would break time rendering in the session reminder modal. When the
 * candidate is missing or invalid we log it and fall back to the browser
 * timezone so the UI degrades gracefully instead of crashing.
 */
export const resolveTimezone = (candidate?: string): string => {
  const fallback = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (!candidate) return fallback;
  try {
    // Throws RangeError for identifiers ICU does not recognise.
    Intl.DateTimeFormat(undefined, { timeZone: candidate });
    return candidate;
  } catch {
    Sentry.captureMessage(
      `Invalid jurisdictionTimezone "${candidate}", falling back to "${fallback}"`,
      { level: Sentry.Severity.Warning },
    );
    return fallback;
  }
};
